import { useState, useEffect, createContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { mtgAPI } from '../apis/mtgAPI';
import { scryAPI } from '../apis/scryAPI';

export const MtgContext = createContext(null);

const FRONTEND_VERSION = '0.7.2';

export const MtgProvider = ({ children }) => {
	const [_symbols, setSymbols] = useState({});
	const [version, setVersion] = useState({
		frontend: FRONTEND_VERSION,
		backend: '',
	});
	const [lastUpdate, setLastUpdate] = useState('');
	const [cutoffPrice, setCutoffPrice] = useState(0.5);

	useEffect(() => {
		console.log('Initialization: MtgProvider');
		// Loading lastUpdate
		mtgAPI.getParams().then((params) => {
			if (params) {
				for (let param_id in params) {
					if (params[param_id].alias === 'last_update_time') {
						setLastUpdate(params[param_id].value);
					}
					if (params[param_id].alias === 'version_database') {
						let versionUpd = {
							frontend: FRONTEND_VERSION,
							backend: params[param_id].value,
						};
						setVersion(versionUpd);
					}
				}
			}
		});
		// Loading symbols
		scryAPI.getSymbols().then((resp) => {
			if (resp) {
				let symbolsUpd = {};
				for (let i = 0; i < resp.length; i++) {
					const symbol = resp[i];
					symbolsUpd[symbol.symbol] = symbol;
				}
				setSymbols(symbolsUpd);
			}
		});
	}, []);

	const autocomplete = useCallback((name, onResult) => {
		scryAPI.autocomplete(name).then((resp) => {
			onResult(resp);
		});
	}, []);

	const searchByName = useCallback((name, onResult) => {
		scryAPI.searchByName(name).then((resp) => {
			onResult(resp);
		});
	}, []);

	const getCardPrints = useCallback((scry_card, onResult) => {
		scryAPI.getCardPrints(scry_card).then((resp) => {
			if (onResult) onResult(resp);
		});
	}, []);

	const getContent = useCallback((alias, onResult) => {
		mtgAPI.getContent(alias).then((resp) => {
			onResult(resp);
		});
	}, []);

	const getShortCondition = useCallback((sample) => {
		if (sample.condition in conditionShort) return conditionShort[sample.condition];
		return '?';
	}, []);

	const getFullCondition = useCallback((sample) => {
		if (sample.condition in conditionFull) return conditionFull[sample.condition];
		return 'Unknown condition';
	}, []);

	const getCardRarity = useCallback((card) => {
		if (card.rarity in cardRarityTitle) {
			return cardRarityTitle[card.rarity];
		}
		return cardRarityTitle[cardRarity.unknown];
	}, []);

	const value = {
		version,
		lastUpdate,
		language,
		searchMinSize: 3,
		cutoffPrice,
		setCutoffPrice,
		getContent,
		getShortCondition,
		getFullCondition,
		getCardPrints,
		getCardRarity,
		autocomplete,
		searchByName,
	};

	return <MtgContext.Provider value={value}>{children}</MtgContext.Provider>;
};

export const language = {
	en: { short: 'Eng', full: 'English' },
	zhs: { short: 'Zhs', full: 'Chinese Simplified' },
	zht: { short: 'Zht', full: 'Chinese Traditional' },
	fr: { short: 'Fre', full: 'French' },
	de: { short: 'Ger', full: 'German' },
	it: { short: 'Ita', full: 'Italian' },
	ja: { short: 'Jpn', full: 'Japanese' },
	ko: { short: 'Kor', full: 'Korean' },
	pt: { short: 'Por', full: 'Portuguese' },
	ru: { short: 'Rus', full: 'Russian' },
	es: { short: 'Spa', full: 'Spanish' },
	//'la': {short: 'Lat', full: 'Latin'},
	//'grc': {short: 'Grc', full: 'Ancient Greek'},
	//'sa': {short: 'San', full: 'Sanskrit'},
	//'ar': {short: 'Ara', full: 'Arabic'},
	//'he': {short: 'He', full: 'Hebrew'},
	//'ph': {short: 'Phy', full: 'Phyrexian'},
};

const cardRarityTitle = {
	0: 'unknown',
	1: 'common',
	2: 'uncommon',
	3: 'rare',
	4: 'mythic',
	5: 'special',
	6: 'bonus',
};

export const cardRarity = {
	unknown: 0,
	common: 1,
	uncommon: 2,
	rare: 3,
	mythic: 4,
	special: 5,
	bonus: 6,
};

export const cardFinish = {
	nonfoil: 0,
	foil: 1,
	etched: 2,
};

export const cardFinishText = {
	0: 'non-foil',
	1: 'foil',
	2: 'etched',
};

export const setType = {
	all: 'all',
	core: 'core',
	expansion: 'expansion',
	masters: 'masters',
	eternal: 'eternal',
	alchemy: 'alchemy',
	masterpiece: 'masterpiece',
	arsenal: 'arsenal',
	from_the_vault: 'from_the_vault',
	spellbook: 'spellbook',
	premium_deck: 'premium_deck',
	duel_deck: 'duel_deck',
	draft_innovation: 'draft_innovation',
	treasure_chest: 'treasure_chest',
	commander: 'commander',
	planechase: 'planechase',
	archenemy: 'archenemy',
	vanguard: 'vanguard',
	funny: 'funny',
	starter: 'starter',
	box: 'box',
	promo: 'promo',
	token: 'token',
	memorabilia: 'memorabilia',
	minigame: 'minigame',
};

export const setTypeInfo = {
	[setType.all]: { title: 'All', description: 'All set types' },
	[setType.core]: { title: 'Core', description: 'A yearly Magic core set (Tenth Edition, etc)' },
	[setType.expansion]: { title: 'Expansion', description: 'A rotational expansion set in a block (Zendikar, etc)' },
	[setType.masters]: {
		title: 'Masters',
		description: 'A reprint set that contains no new cards (Modern Masters, etc)',
	},
	[setType.eternal]: {
		title: 'Eternal',
		description: 'A set of new cards that only get added to high-power formats',
	},
	[setType.alchemy]: { title: 'Alchemy', description: 'An Arena set designed for Alchemy' },
	[setType.masterpiece]: { title: 'Masterpiece', description: 'Masterpiece Series premium foil cards' },
	[setType.arsenal]: { title: 'Arsenal', description: 'A Commander-oriented gift set' },
	[setType.from_the_vault]: { title: 'From the Vault', description: 'From the Vault gift sets' },
	[setType.spellbook]: { title: 'Spellbook', description: 'Spellbook series gift sets' },
	[setType.premium_deck]: { title: 'Premium Deck', description: 'Premium Deck Series decks' },
	[setType.duel_deck]: { title: 'Duel Deck', description: 'Duel Decks' },
	[setType.draft_innovation]: {
		title: 'Draft Innovation',
		description: 'Special draft sets, like Conspiracy and Battlebond',
	},
	[setType.treasure_chest]: { title: 'Treasure Chest', description: 'Magic Online treasure chest prize sets' },
	[setType.commander]: { title: 'Commander', description: 'Commander preconstructed decks' },
	[setType.planechase]: { title: 'Planechase', description: 'Planechase sets' },
	[setType.archenemy]: { title: 'Archenemy', description: 'Archenemy sets' },
	[setType.vanguard]: { title: 'Vanguard', description: 'Vanguard card sets' },
	[setType.funny]: {
		title: 'Funny',
		description: 'A funny un-set or set with funny promos (Unglued, Happy Holidays, etc)',
	},
	[setType.starter]: { title: 'Starter', description: 'A starter/introductory set (Portal, etc)' },
	[setType.box]: { title: 'Box', description: 'A gift box set' },
	[setType.promo]: { title: 'Promo', description: 'A set that contains purely promotional cards' },
	[setType.token]: { title: 'Token', description: 'A set made up of tokens and emblems.' },
	[setType.memorabilia]: {
		title: 'Memorabilia',
		description: 'A set made up of gold-bordered, oversize, or trophy cards that are not legal',
	},
	[setType.minigame]: {
		title: 'Minigame',
		description: 'A set that contains minigame card inserts from booster packs',
	},
};

const conditionShort = {
	0: 'M',
	1: 'NM',
	2: 'LP',
	3: 'MP',
	4: 'HP',
	5: 'D',
};

const conditionFull = {
	0: 'Mint',
	1: 'Near Mint',
	2: 'Lightly Played (Excelent)',
	3: 'Moderately Played (Good)',
	4: 'Heavily Played (Played)',
	5: 'Damaged (Poor)',
};

MtgProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
